import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { sortBy } from 'ramda';
import {
  DesignersByGenderQuery,
  DesignersByGenderQueryVariables,
  DesignersByGenderQuery_designers as Designer
} from '../../generated/types/DesignersByGenderQuery';
import { useCommonSearchApiVariables } from '../../hooks/useCommonSearchApiVariables';

export enum AccountDesignerPreferencesVertical {
  Women = 'women',
  Men = 'men',
  Home = 'home'
}

export const DESIGNERS_BY_GENDER_QUERY = gql`
  query DesignersByGenderQuery($vertical: String!, $abTests: [String], $clientCountryCode: String) {
    designers(gender: $vertical, ab_tests: $abTests, client_country_code: $clientCountryCode) {
      id
      name
    }
  }
`;

const normalizeDesignerName = (designerName: string | null) => {
  const initialLetter = designerName ? designerName[0] : '';

  const normalizedLetter = initialLetter
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

  if (normalizedLetter.match(/[A-Z]/i)) {
    return normalizedLetter;
  }

  return '#';
};

export enum DesignersGroupByOption {
  Alphabetically = 'alphabetically'
}

const groupDesigners = (
  designers: Designer[] | undefined,
  groupByOption: DesignersGroupByOption
) => {
  if (!designers) return undefined;

  const categories: {
    name: string;
    designers: Designer[];
  }[] = [];

  switch (groupByOption) {
    case DesignersGroupByOption.Alphabetically: {
      designers.forEach(designer => {
        const groupName = normalizeDesignerName(designer.name);

        const category = categories.find(({ name }) => groupName === name);

        if (category) {
          category.designers.push(designer);
        } else {
          categories.push({
            name: groupName,
            designers: [designer]
          });
        }
      });

      return categories;
    }

    default:
      return undefined;
  }
};

export const useDesigners = (
  vertical: AccountDesignerPreferencesVertical,
  groupByOption: DesignersGroupByOption
) => {
  const commonSearchApiVariables = useCommonSearchApiVariables();
  const { data, loading, error } = useQuery<
    DesignersByGenderQuery,
    DesignersByGenderQueryVariables
  >(DESIGNERS_BY_GENDER_QUERY, {
    variables: {
      vertical,
      ...commonSearchApiVariables
    }
  });

  const categories = groupDesigners(
    data?.designers
      ? sortBy(designer => designer.name || '', data.designers as Designer[])
      : undefined,
    groupByOption
  );

  return {
    designers: data?.designers as Designer[] | undefined,
    categories: categories ? sortBy(category => category.name, categories) : categories,
    loading,
    error
  };
};
